import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Sun, Moon, Lightbulb, LightbulbOff, Sailboat, Waves } from 'lucide-react'

export function ControlToggles() {
  const [isDarkMode, setIsDarkMode] = useState(false)
  const [isSpotlightEnabled, setIsSpotlightEnabled] = useState(true)
  const [isBoatEnabled, setIsBoatEnabled] = useState(true)

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedDarkMode = localStorage.getItem('theme') === 'dark'
    const savedSpotlight = localStorage.getItem('spotlight-enabled') !== 'false'
    const savedBoat = localStorage.getItem('boat-enabled') !== 'false'
    
    setIsDarkMode(savedDarkMode)
    setIsSpotlightEnabled(savedSpotlight)
    setIsBoatEnabled(savedBoat)
    
    // Apply initial dark mode state
    if (savedDarkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [])

  // Handle dark mode toggle
  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode
    setIsDarkMode(newDarkMode)
    
    if (newDarkMode) {
      document.documentElement.classList.add('dark')
      localStorage.setItem('theme', 'dark')
    } else {
      document.documentElement.classList.remove('dark')
      localStorage.setItem('theme', 'light')
    }
  }

  // Handle spotlight toggle
  const toggleSpotlight = () => {
    const newSpotlight = !isSpotlightEnabled
    setIsSpotlightEnabled(newSpotlight)
    localStorage.setItem('spotlight-enabled', newSpotlight.toString())
    
    // Dispatch custom event for spotlight component
    window.dispatchEvent(new CustomEvent('spotlight-toggle', { 
      detail: { enabled: newSpotlight } 
    }))
  }

  // Handle boat toggle
  const toggleBoat = () => {
    const newBoat = !isBoatEnabled
    setIsBoatEnabled(newBoat)
    localStorage.setItem('boat-enabled', newBoat.toString())
    
    // Dispatch custom event for boat component
    window.dispatchEvent(new CustomEvent('boat-toggle', { 
      detail: { enabled: newBoat } 
    }))
  }

  return (
    <div className="flex items-center gap-2">
      {/* Dark Mode Toggle */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={toggleDarkMode}
        className="p-2 rounded-lg bg-background/50 border border-border/50 hover:bg-accent/50 transition-colors"
        title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {isDarkMode ? (
          <Sun className="h-4 w-4 text-yellow-500" />
        ) : (
          <Moon className="h-4 w-4 text-blue-500" />
        )}
      </motion.button>

      {/* Spotlight Toggle */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={toggleSpotlight}
        className={`p-2 rounded-lg border transition-colors ${
          isSpotlightEnabled 
            ? 'bg-accent/50 border-accent text-accent' 
            : 'bg-background/50 border-border/50 hover:bg-accent/50'
        }`}
        title={isSpotlightEnabled ? 'Disable spotlight effect' : 'Enable spotlight effect'}
      >
        {isSpotlightEnabled ? (
          <Lightbulb className="h-4 w-4" />
        ) : (
          <LightbulbOff className="h-4 w-4" />
        )}
      </motion.button>

      {/* Boat Toggle */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={toggleBoat}
        className={`p-2 rounded-lg border transition-colors ${
          isBoatEnabled 
            ? 'bg-accent/50 border-accent text-accent' 
            : 'bg-background/50 border-border/50 hover:bg-accent/50'
        }`}
        title={isBoatEnabled ? 'Disable sailboat effect' : 'Enable sailboat effect'}
      >
        {isBoatEnabled ? (
          <Sailboat className="h-4 w-4" />
        ) : (
          <Waves className="h-4 w-4" />
        )}
      </motion.button>
    </div>
  )
}
